/**
 * s3UploadService.js
 *
 * All communication with the local Express S3 proxy server.
 * The browser never touches AWS directly — credentials stay server-side.
 *
 * Supported file types: documents, images, audio (mp3/wav/ogg/aac/flac) & video (mp4/webm)
 */

const API_BASE   = import.meta.env.VITE_S3_API_URL || 'http://localhost:4000/api/s3';
const HEALTH_URL = API_BASE.replace('/api/s3', '/api/health');

// ── MIME type normalisation ───────────────────────────────────────────────────
// Some browsers report non-standard MIME types — map them to canonical ones
const MIME_NORMALISE = {
  'audio/mp3':  'audio/mpeg',
  'audio/x-wav':'audio/wav',
  'audio/x-m4a':'audio/mp4',
  'video/x-mp4':'video/mp4',
};

function normaliseType(mimeType) {
  return MIME_NORMALISE[mimeType] || mimeType || 'application/octet-stream';
}

// ── Auth header ───────────────────────────────────────────────────────────────
function getAuthHeaders() {
  const token = localStorage.getItem('ncb_token') || '';
  if (!token) throw new Error('You must be signed in to perform this action.');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ── Shared fetch wrapper ──────────────────────────────────────────────────────
async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (networkErr) {
    throw new Error(
      `Cannot reach the storage server at ${API_BASE}. ` +
      `Make sure the server is running (npm run server) and try again. ` +
      `(${networkErr.message})`
    );
  }

  if (res.status === 429) {
    const retryAfter = res.headers.get('Retry-After') || '60';
    throw new Error(`Too many requests. Please wait ${retryAfter} second(s) before retrying.`);
  }
  if (res.status === 401) {
    throw new Error('Your session has expired or is invalid. Please sign in again.');
  }
  if (res.status === 403) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Access denied: you do not have permission for this action.');
  }
  if (res.status === 503) {
    throw new Error('Storage service is temporarily unavailable. Please try again later.');
  }
  if (res.status === 415) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'This file type is not supported.');
  }
  if (res.status === 413) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'File is too large.');
  }

  return res;
}

// ── Pre-signed upload URL ─────────────────────────────────────────────────────
export async function requestPresignedUploadUrl({ fileName, fileType, fileSize, ownerId, category }) {
  const normType = normaliseType(fileType);

  const res = await apiFetch(`${API_BASE}/presign-upload`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      fileName,
      fileType: normType,
      fileSize,
      ownerId,
      category: category || 'general',
    }),
  });

  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to get pre-signed upload URL.');
  return data;
}

// ── Direct S3 PUT via XHR (supports progress tracking) ───────────────────────
export async function uploadFileToS3(presignedUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status });
      } else {
        reject(new Error(
          `S3 upload failed with HTTP ${xhr.status}. ` +
          `This may indicate a bucket policy issue or an expired pre-signed URL.`
        ));
      }
    });

    xhr.addEventListener('error', () =>
      reject(new Error('Network error during S3 upload. Check your internet connection.'))
    );
    xhr.addEventListener('abort', () =>
      reject(new Error('Upload was aborted.'))
    );
    xhr.addEventListener('timeout', () =>
      reject(new Error('Upload timed out. The file may be too large or your connection too slow.'))
    );

    xhr.open('PUT', presignedUrl, true);
    xhr.setRequestHeader('Content-Type', normaliseType(file.type));
    xhr.timeout = 30 * 60 * 1000; // 30-minute timeout for large audio/video files
    xhr.send(file);
  });
}

// ── Combined: presign → upload → return S3 metadata ──────────────────────────
export async function secureUpload({ file, ownerId, category, onProgress }) {
  const { presignedUrl, s3Key, s3Url, bucket } = await requestPresignedUploadUrl({
    fileName: file.name,
    fileType: file.type || 'application/octet-stream',
    fileSize: file.size,
    ownerId,
    category: category || 'general',
  });

  await uploadFileToS3(presignedUrl, file, onProgress);
  return { s3Key, s3Url, bucket };
}

// ── Pre-signed download URL ───────────────────────────────────────────────────
export async function requestPresignedDownloadUrl({ s3Key, ownerId }) {
  const params = new URLSearchParams({ s3Key, ownerId });
  const res = await apiFetch(`${API_BASE}/presign-download?${params}`, {
    headers: getAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to get pre-signed download URL.');
  return data.presignedUrl;
}

// ── Delete S3 object only (legacy) ───────────────────────────────────────────
export async function deleteS3Object({ s3Key, ownerId }) {
  const res = await apiFetch(`${API_BASE}/delete`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ s3Key, ownerId }),
  });

  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to delete S3 object.');
  return data;
}

// ── Atomic document deletion (S3 + NCB metadata) ─────────────────────────────
export async function deleteDocument({ ownerId, s3Key, recordId }) {
  if (!ownerId) throw new Error('ownerId is required for document deletion.');
  if (!s3Key && recordId === undefined) throw new Error('At least one of s3Key or recordId must be provided.');

  const res = await apiFetch(`${API_BASE}/document`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ownerId, s3Key, recordId }),
  });

  const data = await res.json();
  if (res.status === 200 || res.status === 207) return data;
  throw new Error(data?.error || data?.message || `Document deletion failed with HTTP ${res.status}.`);
}

// ── Multi-party authorization submission ──────────────────────────────────────
export async function submitAuthorization({
  docRecordId,
  ownerId,
  s3Key,
  approverName,
  approverEmail,
  approverRole = 'executor',
  notes = '',
  typedSignature = '',
}) {
  if (!docRecordId)                    throw new Error('docRecordId is required.');
  if (!ownerId)                        throw new Error('ownerId is required.');
  if (!approverName || !approverEmail) throw new Error('approverName and approverEmail are required.');
  if (!typedSignature || typedSignature.trim().length < 2) {
    throw new Error('A typed digital signature (minimum 2 characters) is required.');
  }

  const res = await apiFetch(`${API_BASE}/authorize`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      docRecordId, ownerId, s3Key,
      approverName, approverEmail, approverRole,
      notes,
      typedSignature: typedSignature.trim(),
    }),
  });

  const data = await res.json();

  if (res.status === 409) {
    throw new Error(data.error || 'You have already submitted authorization for this document.');
  }
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Authorization failed with HTTP ${res.status}.`);
  }
  return data;
}

// ── Fetch authorization audit log ─────────────────────────────────────────────
export async function fetchAuthorizations({ docRecordId, ownerId }) {
  if (!docRecordId || !ownerId) throw new Error('docRecordId and ownerId are required.');

  const params = new URLSearchParams({ docRecordId, ownerId });
  const res = await apiFetch(`${API_BASE}/authorizations?${params}`, {
    headers: getAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to fetch authorizations.');
  return data.authorizations;
}

// ── S3 object metadata ────────────────────────────────────────────────────────
export async function fetchS3Metadata({ s3Key, ownerId }) {
  const params = new URLSearchParams({ s3Key, ownerId });
  const res = await apiFetch(`${API_BASE}/metadata?${params}`, {
    headers: getAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to fetch S3 metadata.');
  return data.metadata;
}

// ── Health check ──────────────────────────────────────────────────────────────
export async function checkApiHealth() {
  try {
    const res = await fetch(HEALTH_URL, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return { status: 'error', code: res.status };
    return await res.json();
  } catch (err) {
    return {
      status: 'offline',
      reason: err.name === 'TimeoutError'
        ? 'Server did not respond within 5 seconds.'
        : `Cannot reach server: ${err.message}`,
    };
  }
}

// ── Utility: check if a MIME type is an audio file ───────────────────────────
export function isAudioFile(mimeType) {
  return (mimeType || '').startsWith('audio/');
}

// ── Utility: check if a MIME type is a video file ────────────────────────────
export function isVideoFile(mimeType) {
  return (mimeType || '').startsWith('video/');
}

// ── Utility: human-readable file size ────────────────────────────────────────
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}